import classNames from 'classnames';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AlertDialog from '../../components/AlertDialog/AlertDialog';
import {DialogButton} from '../../components/AlertDialog/DialogContainer';
import FormActions from '../../components/Forms/FormActions';
import {Routes} from '../../routes/routes';
import {Permission} from '../../store/types/Permission';
import {DeviceProfileDetailsTabFormType} from './DeviceProfileDetailsTab.utils';
import {getRoute} from './NewDeviceProfileDialog.utils';
import {makeStyles} from '@material-ui/core/styles';
import {Colors} from '../../styles/Colors';
import {useFormikContext} from 'formik';
import history from '../../history';
import {useDispatch} from 'react-redux';
import {actions} from '../../store';
import {showToast} from '../../utils/showToast';

export const useStyle = makeStyles(() => ({
    actionButtonsContainer: {
        top: 'calc(100vh - 96px)',
        left: 96,
    },
    
    '@media (max-width: 1276px)': {
        actionButtonsContainer: {
            top: 'calc(100vh - 96px)',
            left: 24,
        },
    },
    
    '@media (min-width:1277px) and (max-width: 1440px)': {
        actionButtonsContainer: {
            top: 'calc(100vh - 96px)',
            left: 90,
        },
    },

    '@media (min-width: 1441px)': {
        actionButtonsContainer: {
            top: 'calc(100vh - 96px)',
            left: 96,
        },
    },

    deleteDialog: {
        '& .MuiPaper-root': {
            maxWidth: '365px !important',
        },

        '& .MuiDialogContent-root': {
            minWidth: 'unset',
        },
    },
    
    contentDialog: {
        backgroundColor: Colors.White + ' !important',
        wordBreak: 'break-word'
    },

    copyButtonStyle: {
        width: 'fit-content',
        textWrapMode: 'nowrap',
        cursor: 'pointer'
    },

    copyButtonStyleDisabled: {
        '&.MuiButton-root.Mui-disabled': {
            '& .MuiButton-label': {
                color: Colors.Gray10 + ' !important',
            },
        },
    },
}));

export type DeviceProfileDetailsActionsProps = {
};

const DeviceProfileDetailsActions: React.FC<DeviceProfileDetailsActionsProps> = (
    {
    }) => 
{
    const classes = useStyle();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const onChevronClick = useCallback(() => {
        history.push(Routes.DeviceProfiles);
    }, []);

    const [deleteDialog, setDeleteDialog] = useState<{
        isOpen: boolean;
        i_ua_config_template?: number;
        name?: string;
    }>({
        isOpen: false
    });

    const closeDeleteDialog = () => {
        setDeleteDialog({
            isOpen: false,
            i_ua_config_template: deleteDialog?.i_ua_config_template,
            name: deleteDialog?.name
        });
        setTimeout(() => {
            setDeleteDialog({
                isOpen: false,
                i_ua_config_template: undefined,
                name: undefined
            });
        }, 200);
    };
    
    const handleDelete = (obj: DeviceProfileDetailsTabFormType) => {
        setDeleteDialog({isOpen: true, i_ua_config_template: obj.i_ua_config_template, name: obj.profileName});
    };

    const handleDeleteAction = () => {
        dispatch(actions.deleteProfile.request({
            i_ua_config_template: deleteDialog.i_ua_config_template || 0,
            callback: () => {
                closeDeleteDialog();
                onChevronClick();
                showToast(t<string>('screens:deviceProfiles.profileDeleted'));
            }
        }));
    };
    
    const {
        handleSubmit,
        dirty,
        values
    } = useFormikContext<DeviceProfileDetailsTabFormType>();

    const isCopy = useMemo(() => {
        const url = location.pathname ?? '';
        const pattern = Routes.DeviceProfilesCopy.replace(":id", "(\\d){1,9}");
        const isMatch = url.match(pattern);
        return !!isMatch;
    }, [location]);

    return (
        <div className={classes.actionButtonsContainer}>
            <FormActions
                isSaveActive={dirty || isCopy}
                onSave={() => {
                    handleSubmit();
                }}
                hideCancel={false}
                hideDelete={isCopy}
                editPermission={Permission.Inventory.DeviceProfiles.value}
                deleteTitle={t('screens:deviceProfiles.deleteDeviceProfile')}
                onDelete={() => {values && handleDelete(values);}}
                deletePermission={Permission.Inventory.DeviceProfiles.value}
                cancelButtonDisabled={dirty}
                cancelButtonTitle={isCopy ? t('common:cancel') : t('screens:deviceProfiles.saveAsCopy')}
                cancelButtonClass={classNames(classes.copyButtonStyle, (dirty) && classes.copyButtonStyleDisabled)}
                onCancel={() => {
                    if(isCopy) {
                        history.push(
                            getRoute(Routes.DeviceProfilesDetails, {
                                id: values?.i_ua_config_template,
                            }),
                        );
                    }
                    else {
                        history.push(
                            getRoute(Routes.DeviceProfilesCopy, {
                                id: values?.i_ua_config_template,
                            }),
                        );
                    }
                }}
            />
            <AlertDialog
                className={classes.deleteDialog}
                contentClass={classes.contentDialog}
                isOpen={deleteDialog.isOpen}
                dataQa={'delete-profile-dialog'}
                hideHeader
                description={t('screens:deviceProfiles.deleteRule', {
                    name: deleteDialog.name || '',
                })}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={closeDeleteDialog}
                    />,
                    <DialogButton
                        key="delete"
                        label={t('common:delete')}
                        onClick={handleDeleteAction}
                    />,
                ]}
            />
        </div>
    );
};

export default DeviceProfileDetailsActions;